/* global process */
import store from '../../state';
import { dashboardAuthHeader, selectCurrentDashboard } from '../../state/modules/dashboards/selectors';
import { operations as groupOperations } from '../../state/modules/groups';
import { getAnnouncements } from '../../state/modules/announcements/operations';
import { operations as skillOperations } from '../../state/modules/skills';
import { operations as availabilityCodeOperations } from '../../state/modules/availability-codes';
import { operations as userOperations } from '../../state/modules/users';
import { operations as membershipOperations } from '../../state/modules/memberships';
import { operations as sbdfOperations } from '../../state/modules/standby-duty-functions';
import { operations as taskOperations } from '../../state/modules/tasks';
import { operations as dutyTransactionOperations } from '../../state/modules/duty-transactions';

let instance;

// The DashboardAPIManager is responsible for injecting the right authentication
// credentials in each request. Depending on whether it is available, it injects
// the dashboardReadAuthToken into every request. This token gives accesss to
// regional information, instead of defaulting to the scope of information
// available to the current user. This is needed to show regional widgets, such
// as the ContactListWidget for roles throughout the region.
export default class DashboardAPIManager {
  constructor() {
    if (instance) return instance;
    instance = this;
  }

  authHeader = () => dashboardAuthHeader(store.getState());

  // this is the group scope in which data can be accessed. It is used to limit
  // many API calls below, to increase performance.
  getAccessScopeGroupId = () => selectCurrentDashboard(store.getState())?.access_scope_group_id;

  isTemplate = () => selectCurrentDashboard(store.getState())?.system_template;

  // This function returns a function which dispatches an action and injects the
  // authentication token if available. It simplifies the creation of functions
  // below which dispatch a redux action and adds the authentication header.
  dispatchifyWithGroupId =
    fn =>
    (...args) => {
      if (this.isTemplate()) {
        // don't load data for templates. This is unnecessary and takes a ton of
        // time
        return Promise.resolve();
      }

      // check if we have a param object (preferred). If not, we expect
      // (deprecated) positional params
      if (args.length === 0 || (args.length === 1 && typeof args[0] === 'object' && !Array.isArray(args[0]))) {
        let { params, headers, ...rest } = args[0] || {};

        // inject the current group ID so queries are limited to the objects related
        // to the group. Note that for regional dashboards, the access scope group
        // ID is not the Station ID, but the region ID. This is why we're requesting
        // all objects in the hierarchy. For example, this will load all
        // Announcements in the region, and not only regional announcements.
        params = {
          ...params,
          group_ids: this.getAccessScopeGroupId(),
          in_hierarchy: true
        };
        // inject the dashboard auth header, which may give access to e.g. regional
        // information, even though we're at the level of a Station
        headers = { ...headers, ...this.authHeader() };

        return store.dispatch(fn({ params, headers, ...rest }));
      }

      if (process.env.NODE_ENV !== 'production') {
        // alert('DEPRECATED: use param object instead of positional when calling fetch');
        throw new Error('DEPRECATED: use param object instead of positional when calling fetch');
      }

      let ids, params, headers, forceFetch, replace, onlyMissing;
      [ids, params, headers, forceFetch, replace, onlyMissing] = args;

      console.log('DEPRECATED: use param object instead of positional when calling fetch');
      console.log(ids, params, headers, forceFetch, replace);

      // inject the current group ID so queries are limited to the objects related
      // to the group. Note that for regional dashboards, the access scope group
      // ID is not the Station ID, but the region ID. This is why we're requesting
      // all objects in the hierarchy. For example, this will load all
      // Announcements in the region, and not only regional announcements.
      params = {
        ...params,
        group_ids: this.getAccessScopeGroupId(),
        in_hierarchy: true
      };
      // inject the dashboard auth header, which may give access to e.g. regional
      // information, even though we're at the level of a Station
      headers = { ...headers, ...this.authHeader() };

      return store.dispatch(fn({ ids, params, headers, forceFetch, replace, onlyMissing }));
    };

  fetchGroups = this.dispatchifyWithGroupId(groupOperations.fetch);
  fetchSkills = this.dispatchifyWithGroupId(skillOperations.fetch);
  fetchUsers = this.dispatchifyWithGroupId(userOperations.fetch);
  fetchAvailabilityCodes = this.dispatchifyWithGroupId(availabilityCodeOperations.fetch);
  fetchDutyTransactions = this.dispatchifyWithGroupId(dutyTransactionOperations.fetch);
  fetchMemberships = this.dispatchifyWithGroupId(membershipOperations.fetch);
  fetchStandbyDutyFunctions = this.dispatchifyWithGroupId(sbdfOperations.fetch);
  fetchTasks = this.dispatchifyWithGroupId(taskOperations.fetch);
  getAnnouncements = this.dispatchifyWithGroupId(getAnnouncements);

  dispatchify = fn => (ids, params) => store.dispatch(fn(ids, params, this.authHeader()));

  // this does not take the normal set of params, it uses positional params
  fetchGroupAndSubgroups = this.dispatchify(groupOperations.fetchGroupAndSubgroups);
}
